/**
 * SEO helper hooks. Manipulate document head safely on the client.
 *
 * Used to:
 *   - Set a canonical URL that differs from window.location (to consolidate
 *     duplicate/cannibalizing pages on a single canonical target).
 *   - Mark a page as `noindex` (admin, tracker, thank-you, etc.) without
 *     affecting the rest of the site.
 *
 * All effects are reverted on unmount so SPA navigation doesn't leak meta
 * state from one page to the next.
 */
import { useEffect } from "react";

/**
 * Set `<link rel="canonical" href={url}>` while this component is mounted.
 * Reverts to the apex canonical on unmount.
 */
export function useCanonical(canonicalUrl) {
  useEffect(() => {
    if (!canonicalUrl) return undefined;

    let link = document.querySelector('link[rel="canonical"]');
    const created = !link;
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    const previous = link.getAttribute("href");
    link.setAttribute("href", canonicalUrl);

    return () => {
      if (created) {
        link.remove();
      } else if (previous) {
        link.setAttribute("href", previous);
      }
    };
  }, [canonicalUrl]);
}

/**
 * Set `<meta name="robots" content="noindex, nofollow">` while mounted.
 * Use for pages that must not appear in search results: admin, case tracker,
 * thank-you / receipts, internal utility routes.
 */
export function useNoIndex() {
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    const created = !meta;
    const previous = meta ? meta.getAttribute("content") : null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "noindex, nofollow");

    return () => {
      if (created) {
        meta.remove();
      } else if (previous) {
        meta.setAttribute("content", previous);
      } else {
        meta.removeAttribute("content");
      }
    };
  }, []);
}
